import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInputWithIcon } from "../../components/DropdownInputWithIcon/DropdownInputWithIcon";
import { Utility } from "../../core/Utility";
import { PartnerType, PartnerStatus, Partner } from "../../models/Partner";
import { City } from "../../models/City";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import { createPartner, updateOnePartner } from "../../services/PartnerService";
import {
  getAllCities,
  createCity,
  updateOneCity,
} from "../../services/CityService";
import { getAllUsers } from "../../services/UserService";
import { User } from "../../models/User";
import "./NewPartnerForm.scss";

// show triggers popup by changing the value
export interface NewPartnerFormProps {
  show?: string;
  partner?: Partner;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewCityStyle?: React.CSSProperties;
  buttonInactiveStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

export const NewPartnerForm = (p: NewPartnerFormProps) => {
  const {
    partner,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonNewCityStyle,
    buttonInactiveStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [visibleNewPartnerForm, setVisibleNewPartnerForm] =
    useState<boolean>(false);
  const [citiesApi, setCitiesApi] = useState<City[]>([]);
  const [newPartner, setNewPartner] = useState<Partner>({
    id: partner?.id ?? "",
    isVat: partner?.isVat ?? true,
    type: partner?.type ?? PartnerType.Company,
    code: partner?.code ?? "",
    name: partner?.name ?? "",
    responsiblePersonFirstName: partner?.responsiblePersonFirstName ?? "",
    responsiblePersonLastName: partner?.responsiblePersonLastName ?? "",
    address: partner?.address ?? "",
    city: partner?.city ?? "",
    nationalIdNumber: partner?.nationalIdNumber ?? "",
    nationalIdIssueLocation: partner?.nationalIdIssueLocation ?? "",
    personalCodeNumber: partner?.personalCodeNumber ?? "",
    phone: partner?.phone ?? "",
    mobile: partner?.mobile ?? "",
    email: partner?.email ?? "",
    region: partner?.region ?? "",
    commercialist1: partner?.commercialist1 ?? "",
    commercialist2: partner?.commercialist2 ?? "",
    vatNumber: partner?.vatNumber ?? "",
    companyNumber: partner?.companyNumber ?? "",
    bankName: partner?.bankName ?? "",
    bankIdNumber: partner?.bankIdNumber ?? "",
    bankAccountNumber: partner?.bankAccountNumber ?? "",
    status: partner?.status ?? PartnerStatus.Active,
  });
  const [commercialistOptions, setCommercialistOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [commercialistIIOptions, setCommercialistIIOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [visibleNewCityAddForm, setVisibleNewCityAddForm] =
    useState<boolean>(false);

  const [newCity, setNewCity] = useState<City>({
    name: "",
    code: "",
    id: "",
  });

  const canEdit =
    activeUser.role?.name === RoleType.Superadmin ||
    activeUser.role?.name === RoleType.Administrator ||
    activeUser.role?.name === RoleType.Backoffice ||
    activeUser.role?.name === RoleType.Warehouseman;

  const isValidJmbg = () => {
    Utility.validatePersonalId(newPartner?.personalCodeNumber ?? "");
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const refreshCities = async () => {
    try {
      const resp = await getAllCities();
      const raw = resp?.data?.result ?? resp?.data;
      const list: City[] = Array.isArray(raw) ? raw : [];
      setCitiesApi(list);
    } catch (e) {
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri učitavanju mesta")),
        life: 3000,
      });
    }
  };

  useEffect(() => {
    const load = async () => {
      await refreshCities();

      try {
        const resp = await getAllUsers();
        const raw = resp?.data?.result ?? resp?.data;
        const list: User[] = Array.isArray(raw) ? raw : [];

        const allCommercialist: { label: string; value: string }[] = [];
        const allCommercialistII: { label: string; value: string }[] = [];

        list.forEach((item: any) => {
          const fullName = `${item.firstName ?? ""} ${
            item.lastName ?? ""
          }`.trim();

          if (item.role?.name === RoleType.Commercialist) {
            allCommercialist.push({ label: fullName, value: fullName });
          }

          if (item.role?.name === RoleType.CommercialistII) {
            allCommercialistII.push({ label: fullName, value: fullName });
          }
        });

        setCommercialistOptions(allCommercialist);
        setCommercialistIIOptions(allCommercialistII);
      } catch (e) {
        toast.current?.show({
          severity: "error",
          detail: String(t("Greška pri učitavanju korisnika")),
          life: 3000,
        });
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (show) {
      setVisibleNewPartnerForm(true);
    }
  }, [show]);

  useEffect(() => {
    if (partner) {
      setNewPartner(partner);
      reset();
    }
  }, [partner, reset]);

  const showModal = () => {
    setVisibleNewPartnerForm(!visibleNewPartnerForm ? true : false);
  };

  const handleNewPartnerForm = (prop: string, value: string) => {
    setNewPartner((newPartner) => ({
      ...newPartner,
      [prop]: value,
    }));
  };

  const handleNewPartnerStatus = () => {
    let copyOfNewPartner = cloneDeep(newPartner);
    newPartner.status === PartnerStatus.Active
      ? (copyOfNewPartner.status = PartnerStatus.Blocked)
      : (copyOfNewPartner.status = PartnerStatus.Active);
    setNewPartner(copyOfNewPartner);
  };

  const handleCityChange = (_prop: string, value: any) => {
    let copyOfNewPartner = cloneDeep(newPartner);
    copyOfNewPartner.city = citiesApi.find((item) => item.id == value);
    setNewPartner(copyOfNewPartner);
  };

  const handleNewCity = (prop: string, value: string) => {
    prop === "newCityName"
      ? setNewCity((newCity) => ({ ...newCity, name: value }))
      : setNewCity((newCity) => ({ ...newCity, code: value }));
  };

  const stripEmptyId = <T extends { id?: any }>(obj: T): Omit<T, "id"> | T => {
    const copy: any = { ...obj };

    // obriši id ako je "", null, undefined
    if (copy.id === "" || copy.id === null || copy.id === undefined) {
      delete copy.id;
    }

    return copy;
  };

  const addNewCity = async () => {
    try {
      if (newCity.id) {
        const resp = await updateOneCity(newCity.id, newCity);
        ensureSuccess(resp);
      } else {
        const payload: Partial<City> = stripEmptyId({
          ...newCity,
          code: new Date().getTime().toFixed(),
        });

        const resp = await createCity(payload);
        ensureSuccess(resp);
      }

      setVisibleNewCityAddForm(false);
      setNewCity({ id: "", code: "", name: "" });

      await refreshCities();

      toast.current?.show({
        severity: "success",
        detail: String(t("Mesto je uspešno sačuvano")),
        life: 2500,
      });
    } catch (e) {
      toast.current?.show({
        severity: "error",
        detail: String(t("Greška pri čuvanju mesta")),
        life: 3000,
      });
    }
  };

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const onSubmit = async () => {
    try {
      if (newPartner.id) {
        const resp = await updateOnePartner(newPartner.id, newPartner);
        ensureSuccess(resp);
        toast.current?.show({
          severity: "success",
          detail: String(t("Uspešno sačuvani podaci")),
          life: 3000,
        });
      } else {
        const payload: Partial<Partner> = stripEmptyId(newPartner);

        const resp = await createPartner(payload);
        ensureSuccess(resp);

        toast.current?.show({
          severity: "success",
          detail: t("Uspešno sačuvani podaci"),
        });
      }

      refreshingData?.();

      handleResetModal();
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          String(t("Greška pri čuvanju partnera")),
        life: 3500,
      });
    }
  };

  const onError = () => {
    toast.current?.show({
      severity: "error",
      detail: String(t(`Neispravno popunjena polja`)),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    setNewPartner({
      id: "",
      isVat: true,
      type: PartnerType.Company,
      code: "",
      name: "",
      responsiblePersonFirstName: "",
      responsiblePersonLastName: "",
      address: "",
      city: "",
      nationalIdNumber: "",
      nationalIdIssueLocation: "",
      personalCodeNumber: "",
      phone: "",
      mobile: "",
      region: "",
      commercialist1: "",
      commercialist2: "",
      email: "",
      vatNumber: "",
      companyNumber: "",
      bankName: "",
      bankIdNumber: "",
      bankAccountNumber: "",
      status: PartnerStatus.Active,
    });
    showModal();
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) addButtonSaveStyle = buttonSaveStyle;

  let addButtonNewCityStyle: React.CSSProperties = {};
  if (buttonNewCityStyle) addButtonNewCityStyle = buttonNewCityStyle;

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) addButtonInactiveStyle = buttonInactiveStyle;

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-person-lines-fill" style={{ fontSize: 16 }}></i>
      </span>
      {newPartner.id === "" ? (
        <span>{t(`Dodavanje novog kupca/dobavljača`)}</span>
      ) : (
        <span>{t(`Ažuriranje podataka kupca/dobavljača`)}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Sačuvaj")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={!canEdit}
      />
      <ButtonInput
        labelText={t("Otkaži")}
        onButtonClick={handleResetModal}
        addStyle={addButtonInactiveStyle}
        disabled={!canEdit}
      />
    </div>
  );

  return (
    <>
      <div className="add-form-button">
        <ButtonInput
          labelText={String(buttonLabel ?? "")}
          icon={buttonIcon}
          onButtonClick={showModal}
          addStyle={addButtonStyle}
        />
      </div>
      <Toast ref={toast} position={"top-right"} />
      {visibleNewPartnerForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-partner-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-partner-form-body">
              <TextInputWithIcon
                id="name"
                value={newPartner.name}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Naziv kupca/dobavljača")} *`}
                icon="bi bi-briefcase"
                iconAlign="left"
                placeholderText={t("Unesite naziv kupca/dobavljača")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="code"
                value={newPartner?.code ?? ""}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("SAP šifra kupca/dobavljača")} *`}
                icon="bi bi-file-earmark-lock"
                iconAlign="left"
                placeholderText={t("Unesite šifru kupca/dobavljača")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="isVat"
                value={newPartner.isVat as boolean}
                onValueChange={handleNewPartnerForm}
                options={[
                  { label: t("Da"), value: true },
                  { label: t("Ne"), value: false },
                ]}
                icon="bi bi-file-post"
                filter={false}
                labelText={`${t("U sistemu PDV-a")} *`}
                placeholderText={t("Da li je u sistemu PDV-a")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <DropdownInputWithIcon
                id="type"
                value={newPartner.type as string}
                onValueChange={handleNewPartnerForm}
                options={[
                  { label: t("Privredno društvo"), value: PartnerType.Company },
                  {
                    label: t("Poljoprivredno gazdinstvo"),
                    value: PartnerType.Individual,
                  },
                ]}
                icon="bi bi-clipboard"
                filter={false}
                labelText={`${t("Tip kupca/dobavljača")} *`}
                placeholderText={t("Izaberite tip kupca/dobavljača")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{ required: true }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="responsiblePersonFirstName"
                value={newPartner.responsiblePersonFirstName as string}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Ime kontakt lica")}`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Unesite ime odgovornog lica")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="responsiblePersonLastName"
                value={newPartner.responsiblePersonLastName as string}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Prezime kontakt lica")}`}
                icon="bi bi-person"
                iconAlign="left"
                placeholderText={t("Unesite prezime odgovornog lica")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              {newPartner.type === PartnerType.Individual && (
                <div className="personal-card-content">
                  <TextInputWithIcon
                    id="nationalIdNumber"
                    value={newPartner.nationalIdNumber as string}
                    onValueChange={handleNewPartnerForm}
                    labelText={t("Broj lične karte")}
                    icon="bi bi-person-badge"
                    iconAlign="left"
                    placeholderText={t("Unesite broj lične karte")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{
                      pattern: {
                        value: /[0-9]{9}/,
                        message: "Pogrešan format broja lične karte",
                      },
                    }}
                    disabled={!canEdit}
                  />
                  <TextInputWithIcon
                    id="nationalIdIssueLocation"
                    value={newPartner.nationalIdIssueLocation as string}
                    onValueChange={handleNewPartnerForm}
                    icon="bi bi-person-badge"
                    iconAlign="left"
                    labelText={t("SUP")}
                    placeholderText={t("Unesite SUP")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={!canEdit}
                  />
                </div>
              )}
              {newPartner.type === PartnerType.Individual && (
                <TextInputWithIcon
                  id="personalCodeNumber"
                  value={newPartner.personalCodeNumber as string}
                  onValueChange={handleNewPartnerForm}
                  labelText={t("JMBG korisnika")}
                  icon="bi bi-person-badge"
                  iconAlign="left"
                  placeholderText={t("Unesite JMBG")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    validate: {
                      validJmbg: isValidJmbg,
                    },
                  }}
                  disabled={!canEdit}
                />
              )}
              <TextInputWithIcon
                id="address"
                value={newPartner.address as string}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Adresa kupca/dobavljača")}`}
                icon="bi bi-signpost"
                iconAlign="left"
                placeholderText={t("Unesite adresu kupca/dobavljača")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <div className="city-content">
                <div className="city-id-content">
                  <DropdownInputWithIcon
                    id="city"
                    value={newPartner.city?.id}
                    onValueChange={handleCityChange}
                    options={citiesApi}
                    optionValue="id"
                    optionLabel="name"
                    icon="bi bi-signpost"
                    filter={true}
                    labelText={`${t("Mesto kupca/dobavljača")} *`}
                    placeholderText={t("Unesite mesto kupca/dobavljača")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{ required: true }}
                    disabled={!canEdit}
                  />
                </div>
                <div className="city-add-button">
                  {canEdit && (
                    <ButtonInput
                      labelText={t("Dodaj mesto")}
                      onButtonClick={() => {
                        setVisibleNewCityAddForm(
                          !visibleNewCityAddForm ? true : false
                        );
                      }}
                      icon="bi bi-plus-square"
                      iconAlign="left"
                      addStyle={addButtonNewCityStyle}
                    />
                  )}
                </div>
                {visibleNewCityAddForm && (
                  <div className="new-city-name">
                    <TextInputWithIcon
                      id="newCityName"
                      value={newCity.name}
                      onValueChange={handleNewCity}
                      labelText={t("Naziv mesta/naselja")}
                      icon="bi bi-signpost"
                      iconAlign="left"
                      placeholderText={t("Unesite mesto/naselje")}
                      control={control}
                      errors={errors}
                      disabled={!canEdit}
                    />
                  </div>
                )}
                {visibleNewCityAddForm && (
                  <div className="new-city-add-button">
                    <ButtonInput
                      labelText={t("Dodaj")}
                      onButtonClick={addNewCity}
                      addStyle={addButtonNewCityStyle}
                    />
                  </div>
                )}
              </div>
              <TextInputWithIcon
                id="phone"
                value={(newPartner?.phone as string) ?? ""}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Telefon")}`}
                icon="bi bi-telephone"
                iconAlign="left"
                placeholderText={t("Unesite broj telefona")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /[0-9]{6,}/,
                    message: "Pogrešan format broja fiksnog telefona",
                  },
                }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="mobile"
                value={newPartner.mobile as string}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("Mobilni telefon")}`}
                icon="bi bi-phone"
                iconAlign="left"
                placeholderText={t("Unesite broj mobilnog telefona")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value: /[0-9]{6,}/,
                    message: "Pogrešan format broja mobilnog telefona",
                  },
                }}
                disabled={!canEdit}
              />
              <TextInputWithIcon
                id="email"
                value={newPartner.email as string}
                onValueChange={handleNewPartnerForm}
                labelText={`${t("E - mail adresa")}`}
                icon="bi bi-envelope"
                iconAlign="left"
                placeholderText={t("Unesite e-mail adresu")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: t("Pogrešan format Email-a"),
                  },
                }}
                disabled={!canEdit}
              />
              <div className="vat-id-company-id-content">
                <TextInputWithIcon
                  id="companyNumber"
                  value={newPartner.companyNumber as string}
                  onValueChange={handleNewPartnerForm}
                  labelText={`${
                    newPartner.type === PartnerType.Individual
                      ? `${t("Broj poljoprivrednog gazdinstva")}`
                      : `${t("Matični broj")}`
                  }`}
                  icon="bi bi-file-earmark-binary"
                  iconAlign="left"
                  placeholderText={`${t("Unesite")} ${
                    newPartner.type === PartnerType.Individual
                      ? t("broj poljoprivrednog gazdinstva")
                      : t("matični broj")
                  }`}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
                {newPartner.type === PartnerType.Company && (
                  <TextInputWithIcon
                    id="vatNumber"
                    value={newPartner.vatNumber as string}
                    onValueChange={handleNewPartnerForm}
                    labelText={`${t("PIB")}`}
                    icon="bi bi-file-earmark-binary"
                    iconAlign="left"
                    placeholderText={t("Unesite PIB")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={!canEdit}
                  />
                )}
              </div>
              <DropdownInputWithIcon
                id="region"
                value={newPartner.region as string}
                onValueChange={handleNewPartnerForm}
                options={[
                  { label: "Severna Bačka", value: "Severna Bačka" },
                  { label: "Zapadna Bačka", value: "Zapadna Bačka" },
                  { label: "Južna Bačka", value: "Južna Bačka" },
                  { label: "Severni Banat", value: "Severni Banat" },
                  { label: "Srednji Banat", value: "Srednji Banat" },
                  { label: "Južni Banat", value: "Južni Banat" },
                  { label: "Srem", value: "Srem" },
                  { label: "Grad Beograd", value: "Grad Beograd" },
                  { label: "Mačvanski okrug", value: "Mačvanski krug" },
                  { label: "Kolubarski okrug", value: "Kolubarski okrug" },
                  { label: "Podunavski okrug", value: "Podunavski okrug" },
                  { label: "Braničevski okrug", value: "Braničevski okrug" },
                  { label: "Borski okrug", value: "Borski okrug" },
                  { label: "Pomoravski okrug", value: "Pomoravski okrug" },
                  { label: "Šumadijski okrug", value: "Šumadijski okrug" },
                  { label: "Moravički okrug", value: "Moravički okrug" },
                  { label: "Raški okrug", value: "Raški okrug" },
                  { label: "Rasinski okrug", value: "Rasinski okrug" },
                  { label: "Niški okrug", value: "Niški okrug" },
                  { label: "Toplički okrug", value: "Toplički okrug" },
                ]}
                icon="bi bi-geo-alt"
                filter={false}
                labelText={`${t("Region")}`}
                placeholderText={t("Izaberite region")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <div className="vat-id-company-id-content">
                <DropdownInputWithIcon
                  id="commercialist1"
                  value={newPartner.commercialist1 as string}
                  onValueChange={handleNewPartnerForm}
                  options={commercialistOptions}
                  icon="bi bi-person"
                  filter={false}
                  labelText={`${t("Komercijalista")}`}
                  placeholderText={t("Izaberite komercijalistu")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
                <DropdownInputWithIcon
                  id="commercialist2"
                  value={newPartner.commercialist2 as string}
                  onValueChange={handleNewPartnerForm}
                  options={commercialistIIOptions}
                  icon="bi bi-person"
                  filter={false}
                  labelText={`${t("Komercijalista II")}`}
                  placeholderText={t("Izaberite komercijalistu II")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={!canEdit}
                />
              </div>
              <TextInputWithIcon
                id="bankName"
                value={newPartner.bankName as string}
                onValueChange={handleNewPartnerForm}
                labelText={t("Naziv banke")}
                icon="bi bi-bank"
                iconAlign="left"
                placeholderText={t("Unesite naziv banke")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
              <div className="jib-account-number-content">
                <TextInputWithIcon
                  id="bankIdNumber"
                  value={(newPartner?.bankIdNumber as string) ?? ""}
                  onValueChange={handleNewPartnerForm}
                  labelText={`${t("JIB")}`}
                  icon="bi bi-archive"
                  iconAlign="left"
                  placeholderText={t("JIB")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    pattern: {
                      value: /[0-9]{3}/,
                      message: t("Pogrešan format JIB"),
                    },
                  }}
                  disabled={!canEdit}
                />
                <div className="account-number">
                  <TextInputWithIcon
                    id="bankAccountNumber"
                    value={(newPartner?.bankAccountNumber as string) ?? ""}
                    onValueChange={handleNewPartnerForm}
                    labelText={`${t("Broj računa")}`}
                    icon="bi bi-cash-stack"
                    iconAlign="left"
                    placeholderText={t("Unesite broj računa")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <RadioButtonInput
                id="status"
                value={newPartner.status === PartnerStatus.Active}
                radioButtonValue1={t("Aktivan")}
                radioButtonValue2={t("Blokiran")}
                onValueChange={handleNewPartnerStatus}
                labelText={t("Status partnera")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={!canEdit}
              />
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
